import { useState, useEffect } from 'react'
import {
  Mail,
  Shield,
  User as UserIcon,
  Edit,
  RefreshCcw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Archive,
  RotateCcw,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { motion, AnimatePresence } from 'framer-motion'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useGetUserQuery,
  useInviteUserMutation,
  useUpdateUserStatusMutation,
  User,
} from '@/redux/service/userManagement'
import { Button } from '../ui/button'
import { CustomPagination } from '../CustomPagination'
import { useDebounce } from '@/lib/useDebounce'
import InviteUserModal from './InviteUserModal'
import toast from 'react-hot-toast'
import { showLoadingToast, dismissLoadingToast } from '@/lib/toast'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type StatusFilter = 'active' | 'inactive' | 'all'

interface UsersProps {
  searchTerm: string
  setSearchTerm: (val: string) => void
  page: number
  setPage: (val: number) => void
  sortBy: string | undefined
  setSortBy: (val: string | undefined) => void
  order: 'ASC' | 'DESC' | undefined
  setOrder: (val: 'ASC' | 'DESC' | undefined) => void
  statusFilter: StatusFilter
  setStatusFilter: (val: StatusFilter) => void
  isInviteModalOpen: boolean
  setIsInviteModalOpen: (val: boolean) => void
  selectedUser: User | null
  setSelectedUser: (val: User | null) => void
  handleClearFilters: () => void
  columns: { key: string; label: string }[]
  isColumnVisible: (key: string) => boolean
}

const Users = ({
  searchTerm,
  setSearchTerm,
  page,
  setPage,
  sortBy,
  setSortBy,
  order,
  setOrder,
  statusFilter,
  isInviteModalOpen,
  setIsInviteModalOpen,
  selectedUser,
  setSelectedUser,
  columns,
  isColumnVisible,
}: UsersProps) => {
  const [limit, setLimit] = useState(10)
  const [inviteUser, { isLoading: isInviting }] = useInviteUserMutation()
  const [updateUserStatus, { isLoading: isArchiving }] =
    useUpdateUserStatusMutation()
  const debouncedSearch = useDebounce(searchTerm, 500)

  const [userToArchive, setUserToArchive] = useState<User | null>(null)
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false)

  const visibleColumnsCount =
    columns.filter(c => isColumnVisible(c.key)).length + 1

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') {
        setOrder('DESC')
      } else if (order === 'DESC') {
        setSortBy(undefined)
        setOrder(undefined)
      }
    } else {
      setSortBy(column)
      setOrder('ASC')
    }
    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-2 opacity-50 inline-block' />
    if (order === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-2 inline-block' />
    return <ArrowDown className='w-3 h-3 ml-2 inline-block' />
  }

  const { data, isLoading, isFetching, isError } = useGetUserQuery({
    page,
    limit,
    search: debouncedSearch,
    sort_by: sortBy,
    order,
    status: statusFilter,
  })

  useEffect(() => {
    if (isFetching && !isLoading) {
      showLoadingToast('Loading users...', 'users-fetching-toast')
    } else {
      dismissLoadingToast('users-fetching-toast')
    }
    return () => {
      dismissLoadingToast('users-fetching-toast')
    }
  }, [isFetching, isLoading])

  const users = !isError && data?.success ? data?.data : []
  const totalPages = data?.totalPages || 1
  const totalRecords = data?.total || 0

  const handleEditClick = (user: User) => {
    setSelectedUser(user)
    setIsInviteModalOpen(true)
  }

  const handleModalClose = () => {
    setIsInviteModalOpen(false)
    setSelectedUser(null)
  }

  const handleArchiveClick = (user: User) => {
    setUserToArchive(user)
    setIsArchiveConfirmOpen(true)
  }

  const handleArchiveConfirm = async () => {
    if (!userToArchive) return
    const nextStatus: 'active' | 'inactive' =
      userToArchive.status === 'inactive' ? 'active' : 'inactive'
    try {
      const res = await updateUserStatus({
        user_id: userToArchive.id,
        status: nextStatus,
      }).unwrap()
      toast.success(
        res?.message ||
          (nextStatus === 'inactive'
            ? 'User inactivated successfully'
            : 'User reactivated successfully')
      )
      setIsArchiveConfirmOpen(false)
      setUserToArchive(null)
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to update user status')
    }
  }

  const handleReinvite = async (user: User) => {
    try {
      const res = await inviteUser({
        name: user.name,
        email: user.email,
        role_id: user.role_id,
      }).unwrap()
      toast.success(res?.message || `Invitation resent to ${user.email}`)
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to resend invitation')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
      case 'inactive':
        return 'bg-destructive/10 text-destructive border-destructive/20'
      case 'invited':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20'
      default:
        return 'bg-muted text-muted-foreground border-border'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className='h-full flex flex-col space-y-4 overflow-hidden'
    >
      <div className='flex-1 min-h-0 overflow-auto'>
        <div className='bg-card rounded-sm border border-border shadow-sm'>
          <Table
            wrapperClassName='overflow-visible h-auto'
            className='[&_th]:h-9 [&_th]:px-3 [&_td]:px-3 [&_td]:py-2 border-none'
          >
            <TableHeader className='bg-muted/50 sticky top-0 z-10'>
              <TableRow>
                {columns.map(column => {
                  if (!isColumnVisible(column.key)) return null
                  const sortKeyMap: Record<string, string> = {
                    user: 'user_name',
                    email: 'user_email',
                    role: 'role_name',
                    team: 'team',
                    status: 'status',
                  }
                  const sortKey = sortKeyMap[column.key]
                  return (
                    <TableHead
                      key={column.key}
                      className={`font-bold ${sortKey ? 'cursor-pointer select-none' : ''} ${
                        ['email', 'team'].includes(column.key)
                          ? 'hidden md:table-cell'
                          : ''
                      }`}
                      onClick={sortKey ? () => handleSort(sortKey) : undefined}
                    >
                      {column.label} {sortKey && getSortIcon(sortKey)}
                    </TableHead>
                  )
                })}
                <TableHead className='text-right font-bold'>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence mode='wait'>
                {isLoading ? (
                  Array.from({ length: limit }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      <TableCell colSpan={visibleColumnsCount}>
                        <Skeleton className='h-8 w-full' />
                      </TableCell>
                    </TableRow>
                  ))
                ) : users?.length > 0 ? (
                  users?.map((user: User, index: number) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className='group border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-default'
                    >
                      {columns.map(column => {
                        if (!isColumnVisible(column.key)) return null
                        switch (column.key) {
                          case 'user':
                            return (
                              <TableCell key={column.key}>
                                <div className='flex items-center gap-3'>
                                  <div className='flex flex-col'>
                                    <span className='font-semibold text-foreground group-hover:text-primary transition-colors capitalize truncate'>
                                      {user?.name}
                                    </span>
                                    <span className='text-xs text-muted-foreground md:hidden'>
                                      {user?.email}
                                    </span>
                                  </div>
                                </div>
                              </TableCell>
                            )
                          case 'email':
                            return (
                              <TableCell
                                key={column.key}
                                className='hidden md:table-cell'
                              >
                                <div className='flex items-center gap-2 text-muted-foreground group-hover:text-foreground transition-colors'>
                                  <Mail className='h-3.5 w-3.5' />
                                  <span className='text-sm'>{user?.email}</span>
                                </div>
                              </TableCell>
                            )
                          case 'role':
                            return (
                              <TableCell key={column.key}>
                                <div className='flex items-center gap-2'>
                                  <Shield className='h-3.5 w-3.5 text-muted-foreground' />
                                  <span className='capitalize font-medium text-sm text-foreground/80'>
                                    {user?.role_name}
                                  </span>
                                </div>
                              </TableCell>
                            )
                          case 'team':
                            return (
                              <TableCell
                                key={column.key}
                                className='hidden md:table-cell'
                              >
                                <div className='flex items-center gap-2'>
                                  <span className='capitalize text-sm text-foreground/90'>
                                    {user?.team_name || '—'}
                                  </span>

                                  {user?.is_team_leader && (
                                    <span
                                      className='px-2 py-0.5 text-[10px] font-semibold tracking-wide rounded-full 
                bg-yellow-400/10 text-yellow-400 border border-yellow-400/20'
                                    >
                                      TL
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                            )
                          case 'status':
                            return (
                              <TableCell key={column.key}>
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(user?.status)}`}
                                >
                                  {user?.status}
                                </span>
                              </TableCell>
                            )
                          default:
                            return null
                        }
                      })}
                      <TableCell className='text-right'>
                        {user.status === 'invited' && (
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={() => handleReinvite(user)}
                            disabled={isInviting}
                            className='h-8 w-8 hover:bg-muted hover:text-foreground transition-all'
                          >
                            <RefreshCcw
                              className={`h-5 ${isInviting ? 'animate-spin' : ' '}`}
                            />
                          </Button>
                        )}
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => handleEditClick(user)}
                          className='h-8 w-8 hover:bg-muted hover:text-foreground transition-all'
                          disabled={user.id == 1}
                        >
                          <Edit className='h-5' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => handleArchiveClick(user)}
                          disabled={user.id == 1}
                          title={
                            user.status === 'inactive'
                              ? 'Reactivate user'
                              : 'Inactivate user'
                          }
                          className='h-8 w-8 hover:bg-muted hover:text-foreground transition-all'
                        >
                          {user.status === 'inactive' ? (
                            <RotateCcw className='h-5' />
                          ) : (
                            <Archive className='h-5' />
                          )}
                        </Button>
                      </TableCell>
                    </motion.tr>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className='h-64 text-center'>
                      <div className='flex flex-col items-center justify-center text-muted-foreground gap-2'>
                        <UserIcon className='h-10 w-10 text-muted-foreground/20' />
                        <p className='text-lg font-medium text-muted-foreground/60'>
                          No users found
                        </p>
                        {searchTerm && (
                          <Button
                            variant='link'
                            onClick={() => {
                              setSearchTerm('')
                              setPage(1)
                            }}
                            className='text-primary hover:text-primary/80'
                          >
                            Clear search
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>
      </div>

      {totalPages !== 1 && (
        <div className='shrink-0'>
          <CustomPagination
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            totalPages={totalPages}
            totalRecords={totalRecords}
            title='Users'
          />
        </div>
      )}

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={handleModalClose}
        user={selectedUser}
      />

      <Dialog
        open={isArchiveConfirmOpen}
        onOpenChange={open => {
          setIsArchiveConfirmOpen(open)
          if (!open) setUserToArchive(null)
        }}
      >
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>
              {userToArchive?.status === 'inactive'
                ? 'Reactivate User'
                : 'Inactivate User'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to{' '}
              {userToArchive?.status === 'inactive'
                ? 'reactivate'
                : 'inactivate'}{' '}
              <span className='font-semibold'>{userToArchive?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setIsArchiveConfirmOpen(false)}
              disabled={isArchiving}
            >
              Cancel
            </Button>
            <Button
              variant={
                userToArchive?.status === 'inactive' ? 'default' : 'destructive'
              }
              onClick={handleArchiveConfirm}
              disabled={isArchiving}
            >
              {isArchiving ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}

export default Users
